import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import {Modal, Text, Tooltip} from '@forecast-it/design-system';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {ModalProps} from '../../../forecast-app/shared/components/modals/types';
import {VersionName} from './VersionName';

export interface DeleteBaselineVersionModalPayload {
	versionName: string;
	onConfirm: () => void;
}

/** Leaves room for the words around the name on the title line. */
const NAME_MAX_WIDTH = '80%';

export const showDeleteBaselineVersionModal = (props: DeleteBaselineVersionModalPayload): void => {
	showModal({type: MODAL_TYPE.DELETE_BASELINE_VERSION, ...props});
};

/** "Delete [version name]?" confirmation for removing a baseline version. */
export const DeleteBaselineVersionModal = ({
	closeModal,
	versionName,
	onConfirm,
}: ModalProps<DeleteBaselineVersionModalPayload>): JSX.Element => {
	const {formatMessage} = useIntl();
	const [cut, setCut] = useState(false);
	const confirm = () => {
		onConfirm();
		closeModal();
	};
	const name = <VersionName name={versionName} maxWidth={NAME_MAX_WIDTH} onCutChange={setCut} />;
	return (
		<Modal size={Modal.SIZE.SMALL} onCloseModal={closeModal}>
			<Modal.Title>
				{/* A long name is cut off with an ellipsis so the title stays on one line; hovering it shows the full name. */}
				{formatMessage(
					{id: 'baseline.versions.delete_title'},
					{
						name: cut ? (
							<Tooltip content={versionName} width="260px">
								{/* The tooltip anchors on a host element; an inline one keeps the cut-off width measured against the title. */}
								<span>{name}</span>
							</Tooltip>
						) : (
							name
						),
					}
				)}
			</Modal.Title>
			<Modal.Content>
				<Text>{formatMessage({id: 'baseline.versions.delete_body'})}</Text>
			</Modal.Content>
			<Modal.Footer>
				<Modal.PrimaryFooterButton onPress={confirm} dataCy="baseline-delete-version-confirm">
					{formatMessage({id: 'baseline.versions.delete_version'})}
				</Modal.PrimaryFooterButton>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy="baseline-delete-version-cancel">
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};
